import "./UserDetailsComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import CommonService from "../../../../shared/services/common.service";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikDatePickerComponent from "../../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import formValidationRules from "../../../../shared/services/validation.service";
import FormikSelectDropdownComponent
    from "../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import FromikPhoneNumberComponent
    from "../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import {ImageConfig} from "../../../../constants";

interface UserDetailsComponentProps {
    next: Function;
    cancel: Function;
    userID?: any;
    userDetails?: any;
}

const UserDetailsValidationSchema = Yup.object({
    firstName: Yup.string()
        .required("First Name  is required")
        .max(100, 'First name maximum limit should be 100 characters')
        .nullable(),
    middleName: Yup.string()
        .max(100, 'Middle name maximum limit should be 100 characters')
        .nullable(),
    lastName: Yup.string()
        .required("Last Name  is required")
        .max(100, 'Last name maximum limit should be 100 characters')
        .nullable(),
    dateOfBirth: Yup.date()
        .typeError("Please Enter valid Date")
        .min(new Date(1900, 0, 1), "Date of birth should be greater than 01/01/1900")
        .max(new Date(), "Can not be in the future")
        .nullable(),
    gender: Yup.string().required('Gender is Required').nullable(),
    emailId: Yup.string().required("Email ID is required")
        .email("Invalid Email ID")
        .nullable()
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID"),
    mobileNumber: Yup.string()
        .nullable()
        .required("Mobile Number is Required")
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),

});

interface UserDetailsFormValues {
    firstName: string;
    middleName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    emailId: string;
    mobileNumber: string;
}

const UserDetailsInitialValues: UserDetailsFormValues = {
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    emailId: "",
    mobileNumber: "",
}
const UserDetailsComponent = (props: UserDetailsComponentProps) => {
    const {next, cancel, userID, userDetails} = props;

    const [userDetailsFormValues, setUserDetailsFormValues] = useState<UserDetailsFormValues>(UserDetailsInitialValues);

    const onFormSubmit = useCallback((values: UserDetailsFormValues, {
        setSubmitting,
        setErrors,
        validateForm
    }: FormikHelpers<any>) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to continue adding this User?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: "Continue",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        }).then(() => {
            const payload = {...values, emailId: values?.emailId?.toLowerCase()};
            payload.mobileNumber = payload.mobileNumber?.replace(/[^0-9]/g, "");
            setSubmitting(true);
            if (userID) {
                CommonService._user.editUser(userID, payload)
                    .then((response: any) => {
                        setSubmitting(false);
                        next(response?.data?.id, response?.data);
                    })
                    .catch((error: any) => {
                        setSubmitting(false);
                        CommonService.handleErrors(setErrors, error);
                    })
            } else {
                CommonService._user.addUser(payload)
                    .then((response: any) => {
                        setSubmitting(false);
                        next(response?.data?.id, response?.data);
                    })
                    .catch((error: any) => {
                        setSubmitting(false);
                        CommonService.handleErrors(setErrors, error);
                    })
            }
        }).catch(() => {
            setSubmitting(false);
            // do nothing;
        });
    }, [next, userID]);

    useEffect(() => {
        if (userDetails) {
            const tempUserDetails = {
                ...userDetails,
                mobileNumber: CommonService.getMaskedMobileNumber(userDetails?.mobileNumber)
            }
            setUserDetailsFormValues(tempUserDetails);
        }
    }, [userDetails]);

    return (
        <div className={'user-details-component  screen t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'User Details'}
                tooltipData={"Ensure that the inputs provided are accurate."}
            />
            <div className="t-form">
                <Formik
                    initialValues={userDetailsFormValues}
                    validationSchema={UserDetailsValidationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validateOnMount={true}
                    enableReinitialize={true}
                    onSubmit={onFormSubmit}
                >
                    {({isSubmitting, errors, values, isValid, setFieldValue, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values, errors]);
                        return (
                            <Form noValidate={true} className={"patient-basic-details-add-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <div className="form-sub-title">Personal Details</div>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'firstName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'First Name'}
                                                            placeholder={'Enter First Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'middleName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Middle Name'}
                                                            placeholder={'Enter Middle Name'}
                                                            type={"text"}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'lastName'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Last Name'}
                                                            placeholder={'Enter Last Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'dateOfBirth'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            label={'Date of Birth'}
                                                            formikField={field}
                                                            required={false}
                                                            maxDate={formValidationRules.eighteenYearsAgo}
                                                            placeholder={'Enter Date of Birth'}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>
                                            <Field name={'gender'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Gender'}
                                                            options={CommonService._staticData.genderOptions}
                                                            placeholder={'Select Gender'}
                                                            valueKey={'code'}
                                                            required={true}
                                                            getOptionLabel={(option) => (option.title)}
                                                            formikField={field}/>
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} xl={6}>

                                        </Grid>
                                    </Grid>
                                    <div className="horizontal-line"/>
                                    <div className="form-sub-title">Contact Details</div>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'emailId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Email ID'}
                                                            placeholder={'Enter Email ID'}
                                                            type={"email"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'mobileNumber'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FromikPhoneNumberComponent
                                                            label={'Mobile Number'}
                                                            placeholder={'(000) 000-0000'}
                                                            maxLength={14}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div className="form-action-section">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <ButtonComponent
                                                type="button"
                                                color={"primary"}
                                                variant={"outlined"}
                                                fullWidth={true}
                                                disabled={isSubmitting}
                                                handleClick={cancel}
                                            >
                                                Cancel
                                            </ButtonComponent>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <ButtonComponent
                                                type="submit"
                                                color={"primary"}
                                                variant={"contained"}
                                                fullWidth={true}
                                                disabled={!isValid}
                                                isLoading={isSubmitting}
                                            >
                                                Proceed
                                            </ButtonComponent>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );

};

export default UserDetailsComponent;
